import React, { ReactNode } from 'react';
import classnames from 'classnames';
import TickSvg from '../../components/svg/picto/tickSvg';
import { H5 } from '../../primitives/appTitle';
import { getRecordValues } from '../../utils/lodashUtils';
import {
  computePhotoTotalNb,
  computePhotoTotalPrice,
  computePhotoTotalTime,
} from '../order/utils/orderPageUtils';
import { FrontCommonMessages } from '../../appCommonTranslations';
import { ShText, useShTranslate } from '@shoootin/translations';
import { getFrontMessage } from '../../appMessageDefinitions';
import {
  EmptyScanConfig,
  EmptySelectedOffers,
} from '../order/context/orderPageContextState';
import { useNavigate } from '../../primitives/appLink';
import { ShCountryConfigs, ShCurrency, ShShootingType } from '@shoootin/config';
import { mapValues } from 'lodash';
import { useFrontCountry } from '../../layout/frontContext';
import { ShPrice } from '@shoootin/components-web';
import {
  ShOfferDTO,
  ShOfferPhotoDTO,
  ShOfferScanDTO,
  ShOfferVideoDTO,
} from '@shoootin/api';
import { minuteToHours } from '@shoootin/utils';

const useOfferSlideCountry = () => useFrontCountry(); // TODO should be from offer

const OfferSliderOrderBtn = ({ navigateFn }: { navigateFn: () => void }) => {
  return (
    <button
      className="btn large"
      onClick={async (e) => {
        e.preventDefault();
        navigateFn();
      }}
    >
      <span>
        <ShText message={getFrontMessage('common_actions_order')} />
      </span>
      <TickSvg />
    </button>
  );
};

const OfferCommonSlide = ({ children }: { children: ReactNode }) => {
  return (
    <div
      className="slider-tile swiper-slide"
      style={{
        display: 'flex',
        width: '100%',
        height: 'auto',
      }}
    >
      <div
        className="slider-content"
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const OfferPhotoSlide = ({
  offer,
  currency,
  shootingType,
}: {
  offer: ShOfferPhotoDTO;
  currency: ShCurrency;
  shootingType: ShShootingType;
}) => {
  const defaultOptionsList = getRecordValues(offer.photo.defaultOptions);
  const translate = useShTranslate();
  // We compute the number/price/time, including the default options
  const nb = computePhotoTotalNb(offer, defaultOptionsList);
  const price = computePhotoTotalPrice(offer, defaultOptionsList);
  const time = computePhotoTotalTime(offer, defaultOptionsList);

  const label = `${translate(FrontCommonMessages.nPhotos, { n: nb })} — ${
    offer.name
  }`;
  const navigate = useNavigate();
  const country = useOfferSlideCountry();

  const navigateFn = () =>
    navigate.toOrder({
      country,
      selectedOffers: {
        ...EmptySelectedOffers,
        photoOfferId: offer.id,
        photoConfig: {
          options: mapValues(offer.photo.defaultOptions, (o) =>
            o ? o.id : undefined,
          ),
        },
      },
      shootingType,
    });
  return (
    <OfferCommonSlide>
      <div
        className={classnames('slider-tile__header', {
          inline: !offer.baseTime,
        })}
      >
        <H5>{label}</H5>
        <div className="slider-tile__legend">
          {time && minuteToHours(time)}
          <span className="slider-tile__price">
            <ShPrice price={price} currency={currency} />
          </span>
        </div>
      </div>
      <p style={{ flex: '1 0 auto' }}>{offer.description}</p>
      <OfferSliderOrderBtn navigateFn={navigateFn} />
    </OfferCommonSlide>
  );
};

const OfferVideoSlide = ({
  offer,
  currency,
  shootingType,
}: {
  offer: ShOfferVideoDTO;
  currency: ShCurrency;
  shootingType: ShShootingType;
}) => {
  const label = offer.name;
  const navigate = useNavigate();
  const country = useOfferSlideCountry();

  const navigateFn = () =>
    navigate.toOrder({
      country,
      selectedOffers: {
        ...EmptySelectedOffers,
        videoOfferId: offer.id,
      },
      shootingType,
    });

  return (
    <OfferCommonSlide>
      <div
        className={classnames('slider-tile__header', {
          inline: !offer.baseTime,
        })}
      >
        <H5>{label}</H5>
        <div className="slider-tile__legend">
          {offer.baseTime && minuteToHours(offer.baseTime)}
          <span className="slider-tile__price">
            <ShPrice price={offer.basePrice} currency={currency} />
          </span>
        </div>
      </div>
      <p style={{ flex: '1 0 auto' }}>{offer.description}</p>
      <OfferSliderOrderBtn navigateFn={navigateFn} />
    </OfferCommonSlide>
  );
};

const OfferScanSlide = ({
  offer,
  currency,
  shootingType,
  index,
}: {
  offer: ShOfferScanDTO;
  currency: ShCurrency;
  shootingType: ShShootingType;
  index: number;
}) => {
  const translate = useShTranslate();

  const label = translate(FrontCommonMessages.matterport);
  const range = offer.scan.priceRanges[index];
  const time =
    offer.scan.timeCalculatorInitWith3D +
    offer.scan.timeCalculatorMultiWith3D * range.lowerRange;

  const navigate = useNavigate();
  const country = useOfferSlideCountry();
  const metricSystem = ShCountryConfigs[country].metricSystem;

  const navigateFn = () =>
    navigate.toOrder({
      country,
      selectedOffers: {
        ...EmptySelectedOffers,
        scanOfferId: offer.id,
        scanConfig: {
          ...EmptyScanConfig,
          surface: range.lowerRange,
        },
      },
      shootingType,
    });

  const description = translate(FrontCommonMessages.matterportSurfaceBetween, {
    low: (
      <b>
        {range.lowerRange}{' '}
        {metricSystem ? (
          <>
            m<sup>2</sup>
          </>
        ) : (
          <>sqft</>
        )}
      </b>
    ),
    high: (
      <b>
        {range.upperRange - 1}{' '}
        {metricSystem ? (
          <>
            m<sup>2</sup>
          </>
        ) : (
          <>sqft</>
        )}
      </b>
    ),
  });

  return (
    <OfferCommonSlide>
      <div
        className={classnames('slider-tile__header', {
          inline: false,
        })}
      >
        <H5>{index === 0 ? label : '\u00a0'}</H5>
        <div className="slider-tile__legend">
          <span />
          <span className="slider-tile__price">
            <ShPrice price={range.priceWith3D} currency={currency} />
          </span>
        </div>
      </div>
      <p style={{ flex: '1 0 auto' }}>{description}</p>
      <OfferSliderOrderBtn navigateFn={navigateFn} />
    </OfferCommonSlide>
  );
};

const OfferSlide = ({
  offer,
  currency,
  shootingType,
  index,
}: {
  offer: ShOfferDTO;
  currency: ShCurrency;
  shootingType: ShShootingType;
  index: number;
}) => {
  if (offer.type == 'photo') {
    return (
      <OfferPhotoSlide
        shootingType={shootingType}
        offer={offer as ShOfferPhotoDTO}
        currency={currency}
      />
    );
  } else if (offer.type == 'video') {
    return (
      <OfferVideoSlide
        shootingType={shootingType}
        offer={offer as ShOfferVideoDTO}
        currency={currency}
      />
    );
  } else if (offer.type == 'scan') {
    return (
      <OfferScanSlide
        shootingType={shootingType}
        offer={offer as ShOfferScanDTO}
        currency={currency}
        index={index}
      />
    );
  } else return <></>;
};

export default OfferSlide;
